// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable formatjs/no-literal-string-in-jsx */
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageNotFound } from "#/components/views/error/PageNotFound"
import { Title } from "#/services/page-title/Title"
import { Paths } from "#/utils/Paths"
import { reportErrorMessage } from "#/utils/ErrorReporting"
import { Alert, Box, Progress } from "@carrotfertility/carotene-core"
import { IntakeForm, PhotoIdUpload, SidebarAppointmentsTile } from "@internal/telemedicine"
import React from "react"
import { Redirect, useParams } from "react-router-dom"
import type { IntakeSubmittedLocationState } from "#/pages/telemedicine/IntakeSubmittedDialog"
import { usePreVisitIntake } from "./hooks/usePreVisitIntake"
import { IntakeTrackingProvider } from "./IntakeTrackingProvider"
import { PreVisitFormTracking } from "./PreVisitFormTracking"
import { PreVisitIntakeTracking } from "./PreVisitIntakeTracking"

const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : "unknown error")

const CenteredProgress = () => (
  <Box
    sx={(theme) => ({
      marginBlockStart: theme.tokens.spacing.md,
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    })}
  >
    <Progress />
  </Box>
)

export function TelemedicinePreVisitIntakePage() {
  const { id: appointmentId } = useParams<{ id: string }>()
  return (
    <IntakeTrackingProvider key={appointmentId} appointmentId={appointmentId}>
      <PreVisitContent appointmentId={appointmentId} />
    </IntakeTrackingProvider>
  )
}

function PreVisitContent({ appointmentId }: { appointmentId: string }) {
  const { access, questionnaire, photoId } = usePreVisitIntake(appointmentId)

  if (access.isLoading) {
    return null
  }

  if (access.isDenied) {
    return <Redirect to={Paths.HOME} />
  }

  // NOT_FOUND means the appointment isn't the member's or is in the past.
  if (questionnaire.isNotFound) {
    return <PageNotFound />
  }

  const submittedState: IntakeSubmittedLocationState = { intakeQuestionnaireSubmitted: true }
  const redirectToConfirmationModal = <Redirect to={{ pathname: Paths.HOME, state: submittedState }} />

  if (questionnaire.isComplete) {
    return questionnaire.hasForms === false ? <PageNotFound /> : redirectToConfirmationModal
  }

  const renderQuestionnaire = (): JSX.Element => {
    const { form } = questionnaire
    if (!form) return <Alert severity="error">No intake form is connected to this appointment.</Alert>
    return (
      <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
        <IntakeForm
          key={`${appointmentId}:${form.id}`}
          form={form}
          submitError={questionnaire.hasSubmissionError}
          onError={reportErrorMessage}
          warnBeforeExit
          onSubmit={questionnaire.submitAnswers}
        >
          <PreVisitFormTracking />
        </IntakeForm>
      </Box>
    )
  }

  const renderIdStep = (): JSX.Element => {
    if (photoId.isLoading) return <CenteredProgress />
    if (photoId.isRequired) {
      return (
        <>
          <PhotoIdUpload
            onImageAdded={photoId.trackImageAdded}
            onSubmit={photoId.upload}
            submitting={photoId.isUploading}
          />
          {photoId.uploadError ? (
            <Alert severity="error">ID upload failed: {errorMessage(photoId.uploadError)}</Alert>
          ) : null}
        </>
      )
    }
    return renderQuestionnaire()
  }

  const renderContent = (): JSX.Element => {
    if (questionnaire.isLoading) return <CenteredProgress />

    if (questionnaire.error) {
      return <Alert severity="error">Something went wrong loading these questions. Please try again.</Alert>
    }

    return renderIdStep()
  }

  return (
    <PageLayout
      variant="sidebar"
      sidebar={<SidebarAppointmentsTile appointmentsPath={Paths.TELEMEDICINE_APPOINTMENTS} />}
    >
      <Title title="Prepare for your visit" />
      {questionnaire.isReady ? <PreVisitIntakeTracking program={questionnaire.program} /> : null}
      {renderContent()}
    </PageLayout>
  )
}
